import { UserInputError } from "./errors-CEDWDXew.js";
import { ParamOrganizationSlug, ParamPeriod, ParamProjectSlug, ParamRegionUrl } from "./schema-DKEjeVqP.js";
import { PUBLIC_EVENTS_DATASETS, isMetricsDataset, normalizeEventsDataset } from "./events-datasets-Cu37u43g.js";
import { extractConversationIdFromSearchQuery } from "./url-utils-CYEhmNHi.js";
import { defineTool } from "./define-SD6AQa4b.js";
import { apiServiceFromContext } from "./api-EaEPyKlQ.js";
import { hasAgentProvider } from "./provider-factory-Cs_2DBhe.js";
import { withProviderFallback } from "./provider-fallback-L61Pbfxx.js";
import { RECOMMENDED_FIELDS, TRACE_METRICS_SAMPLE_IDENTITY_FIELDS } from "./config-BqCCImAX.js";
import { formatEventsValidationResults, isAggregateQuery, isSemanticFilterDowngrade, looksLikeSentrySearchSyntax, recordEventsSearchValidationTelemetry, validateEventsSearch } from "./utils-Dg2aeBEb.js";
import { searchEventsAgent } from "./agent-BCH9XRWB.js";
import { formatErrorResults, formatLogResults, formatProfileResults, formatSpanResults, formatTraceMetricsResults } from "./formatters-Cuh2gD9M.js";
import { DEFAULT_REPLAY_SORT, DEFAULT_REPLAY_STATS_PERIOD, formatReplayResults, isValidReplaySort } from "./replays-Dg9Syytd.js";
import { z } from "zod";
import { getActiveSpan, setTag } from "@sentry/core";

//#region src/tools/catalog/search-events.ts
const SEARCH_EVENTS_DATASETS = [...PUBLIC_EVENTS_DATASETS, "replays"];
const DEFAULT_EVENTS_SORT = "-timestamp";
function defaultSortForDataset(dataset) {
	return dataset === "replays" ? DEFAULT_REPLAY_SORT : DEFAULT_EVENTS_SORT;
}
function defaultFieldsForDataset(dataset) {
	return RECOMMENDED_FIELDS[normalizeEventsDataset(dataset)].basic;
}
function resolveEventFields({ dataset, explicitFields, agentFields, trustExplicitFields }) {
	if (trustExplicitFields && explicitFields && explicitFields.length > 0) return explicitFields;
	if (agentFields && agentFields.length > 0) return agentFields;
	return defaultFieldsForDataset(dataset);
}
function parseAgentTimeRange(timeRange) {
	if (typeof timeRange !== "object" || timeRange === null) return void 0;
	if ("statsPeriod" in timeRange && typeof timeRange.statsPeriod === "string") return { statsPeriod: timeRange.statsPeriod };
	if ("start" in timeRange && "end" in timeRange && typeof timeRange.start === "string" && typeof timeRange.end === "string") return {
		start: timeRange.start,
		end: timeRange.end
	};
	return void 0;
}
function augmentFieldsWithSort(fields, sort) {
	const sortField = sort.startsWith("-") ? sort.slice(1) : sort;
	const sortIsAggregate = sortField.includes("(") && sortField.includes(")");
	if (sortField && !fields.includes(sortField) && (sortIsAggregate || !isAggregateQuery(fields))) return [...fields, sortField];
	return fields;
}
function buildRequestFields(dataset, fields) {
	return dataset !== "replays" && isMetricsDataset(dataset) && !isAggregateQuery(fields) ? Array.from(new Set([...fields, ...TRACE_METRICS_SAMPLE_IDENTITY_FIELDS])) : fields;
}
function isTraceItemDataset(dataset) {
	return dataset === "spans" || dataset === "logs" || dataset === "metrics";
}
function hasFields(fields) {
	return Array.isArray(fields) && fields.length > 0;
}
function formatSearchValue(value) {
	return /^[^\s"',[\]]+$/.test(value) ? value : JSON.stringify(value);
}
function formatEnvironmentFilter(environment) {
	if (!environment) return void 0;
	const environments = Array.isArray(environment) ? environment : [environment];
	if (environments.length === 0) return void 0;
	if (environments.length === 1) {
		const environmentValue = environments[0];
		return environmentValue === void 0 ? void 0 : `environment:${formatSearchValue(environmentValue)}`;
	}
	return `environment:[${environments.map(formatSearchValue).join(",")}]`;
}
function appendSearchFilter(query, filter) {
	const trimmedQuery = query.trim();
	if (!filter) return trimmedQuery;
	if (tokenizeSearchQuery(trimmedQuery).includes(filter)) return trimmedQuery;
	return [trimmedQuery, filter].filter(Boolean).join(" ");
}
function applyEnvironmentToEventsQuery(dataset, query, environment) {
	if (dataset === "replays") return query;
	return appendSearchFilter(query, formatEnvironmentFilter(environment));
}
function tokenizeSearchQuery(query) {
	const tokens = [];
	let currentToken = "";
	let quote = null;
	let escaped = false;
	for (const char of query) {
		if (escaped) {
			currentToken += char;
			escaped = false;
			continue;
		}
		if (char === "\\") {
			currentToken += char;
			escaped = true;
			continue;
		}
		if (quote) {
			currentToken += char;
			if (char === quote) quote = null;
			continue;
		}
		if (char === "\"" || char === "'") {
			currentToken += char;
			quote = char;
			continue;
		}
		if (/\s/.test(char)) {
			if (currentToken) {
				tokens.push(currentToken);
				currentToken = "";
			}
			continue;
		}
		currentToken += char;
	}
	if (currentToken) tokens.push(currentToken);
	return tokens;
}
function containsSearchToken(query, token) {
	const escapedToken = token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
	return (/* @__PURE__ */ new RegExp(`(^|\\s)${escapedToken}(?=\\s|$)`)).test(query);
}
function preservesSearchTokens(originalQuery, repairedQuery) {
	return tokenizeSearchQuery(originalQuery).every((token) => containsSearchToken(repairedQuery, token));
}
function choosePreservingRepairedQuery(params) {
	const originalQuery = params.originalQuery.trim();
	const repairedQuery = params.repairedQuery?.trim();
	if (!repairedQuery) return appendSearchFilter(originalQuery, params.filter);
	if (isSemanticFilterDowngrade(originalQuery, repairedQuery)) return appendSearchFilter(originalQuery, params.filter);
	if (!originalQuery || preservesSearchTokens(originalQuery, repairedQuery)) return appendSearchFilter(repairedQuery, params.filter);
	return appendSearchFilter(originalQuery, params.filter);
}
function buildAgentPrompt(params) {
	return [
		"Translate this Sentry event search request.",
		"The query may be natural language or already-valid Sentry search syntax.",
		"Preserve valid explicit parameters, but correct dataset, query syntax, fields, sort, and time range when they conflict or would fail.",
		"If the user query already uses Sentry search syntax, treat its filters as authoritative unless validateSearch proves a field is invalid.",
		"Never replace a structured field filter with message/log.body/full-text matching. If no valid attribute exists for an explicit field:value filter, keep the field and let validation fail.",
		"For spans, logs, and metrics, use datasetAttributes to discover likely fields with substringMatch, query, and attributeTypes before dropping or renaming explicit fields.",
		"A broad datasetAttributes result may be truncated, so absence from that preview does not prove an explicit field is invalid.",
		"For non-replay datasets, call validateSearch on the candidate request and fix failures in this same pass before returning.",
		"For non-replay datasets, convert environment parameters into query filters. For replays, keep environment in the separate environment parameter.",
		"",
		`User query: ${params.query || "(empty)"}`,
		"Current parameters:",
		JSON.stringify({
			dataset: params.dataset,
			fields: params.fields ?? null,
			sort: params.sort ?? null,
			statsPeriod: params.statsPeriod ?? null,
			environment: params.environment ?? null
		}, null, 2)
	].join("\n");
}
var search_events_default = defineTool({
	name: "search_events",
	skills: [
		"inspect",
		"triage",
		"seer"
	],
	requiredScopes: ["event:read"],
	description: [
		"Search Sentry events and replays. Use for event counts/statistics.",
		"",
		"`query` can be natural language or Sentry search syntax. With an agent configured, it fixes dataset, query, fields, and sort before running.",
		"",
		"Supports TWO query types:",
		"1. AGGREGATIONS (counts, sums, averages): 'how many errors', 'total tokens'",
		"2. Individual events with timestamps: 'error logs from last hour'",
		"",
		"Datasets:",
		"- errors: Exception/crash events with stack traces, usually grouped into issues",
		"- logs: Application log entries, including error-severity log messages",
		"- spans: Raw trace/span events for performance, AI/LLM calls, requests, and operations",
		"- metrics: Metric rows and aggregates: counters, gauges, distributions, values",
		"- profiles: Transaction/continuous profile results, profile IDs, profiled transactions",
		"- replays: Session replay results: rage clicks, dead clicks, visited pages, replay users",
		"If the user says logs, log messages, error logs, or warning logs, choose logs instead of errors.",
		"",
		"Replay searches return replay lists only; replay count()/avg()/sum() are not supported.",
		"",
		"NOT for grouped issue lists (use search_issues) or app screenshots/images (use get_latest_base_snapshot).",
		"",
		"<examples>",
		"search_events(organizationSlug='my-org', query='how many errors today')",
		"search_events(organizationSlug='my-org', dataset='errors', query='level:error')",
		"search_events(organizationSlug='my-org', dataset='errors', fields=['issue', 'count()'], sort='-count()')",
		"search_events(organizationSlug='my-org', dataset='spans', query='span.op:db', sort='-span.duration')",
		"search_events(organizationSlug='my-org', dataset='replays', query='count_errors:>0', sort='-count_errors')",
		"</examples>",
		"",
		"<hints>",
		"- If the user passes a parameter in the form of name/otherName, it's likely in the format of <organizationSlug>/<projectSlug>.",
		"- Parse org/project notation directly without calling find_organizations or find_projects.",
		"- Use fields with aggregate functions like count(), avg(), sum() for statistics",
		"- Sort by -count() for most common, -timestamp for newest",
		"</hints>"
	].join("\n"),
	inputSchema: {
		organizationSlug: ParamOrganizationSlug,
		dataset: z.enum(SEARCH_EVENTS_DATASETS).optional().describe("Initial dataset hint: errors, logs, spans, metrics, profiles, or replays. The agent may correct this when configured."),
		query: z.string().trim().optional().describe("Natural language or Sentry event search query syntax."),
		fields: z.array(z.string()).nullable().optional().describe("Fields to return for event datasets. If not specified, uses sensible defaults. Include aggregate functions like count(), avg() for statistics. Leave null for dataset='replays'."),
		sort: z.string().trim().nullable().optional().describe("Sort field (prefix with - for descending). If omitted, event datasets default to -timestamp and replays default to -started_at. Use -count() for event aggregations. For dataset='replays', use replay sorts like -started_at or -count_errors."),
		projectSlug: ParamProjectSlug.nullable().default(null),
		environment: z.union([z.string().trim().min(1), z.array(z.string().trim().min(1)).min(1)]).optional().describe("Optional environment filter for dataset='replays'. Use a string for one environment or an array for multiple. Omit when unused. For other datasets, filter environment in the query string instead."),
		period: ParamPeriod.optional(),
		regionUrl: ParamRegionUrl.nullable().default(null),
		limit: z.number().min(1).max(100).default(10).describe("Maximum number of results to return (1-100)"),
		includeExplanation: z.boolean().default(false).describe("Include explanation of how the query was translated or repaired")
	},
	annotations: {
		readOnlyHint: true,
		destructiveHint: false,
		openWorldHint: true
	},
	async handler(params, context) {
		const apiService = apiServiceFromContext(context, { regionUrl: params.regionUrl ?? void 0 });
		const organizationSlug = params.organizationSlug;
		setTag("organization.slug", organizationSlug);
		if (params.projectSlug) setTag("project.slug", params.projectSlug);
		const inputDataset = params.dataset ?? "errors";
		const hasStructuredQuery = looksLikeSentrySearchSyntax(params.query);
		const canApplyEnvironmentFilter = inputDataset !== "replays" && isTraceItemDataset(inputDataset) && hasStructuredQuery;
		if (!hasAgentProvider() && inputDataset !== "replays" && params.environment && !canApplyEnvironmentFilter) throw new UserInputError("The `environment` parameter is only supported for dataset='replays'. For other datasets, include environment filtering in the query string instead.");
		let projectId;
		if (params.projectSlug) {
			const project = await apiService.getProject({
				organizationSlug,
				projectSlugOrId: params.projectSlug
			});
			projectId = String(project.id);
		}
		let dataset;
		let sentryQuery;
		let fields;
		let sortParam;
		let timeParams;
		let explanation;
		let environment = params.environment;
		const explicitSort = params.sort?.trim() || void 0;
		const hasExplicitDataset = params.dataset !== void 0;
		const hasExplicitFields = hasFields(params.fields);
		const hasExplicitSort = explicitSort !== void 0;
		const hasExplicitPeriod = params.period !== void 0;
		const hasExplicitTraceItemDataset = hasExplicitDataset && isTraceItemDataset(inputDataset);
		const shouldTrustStructuredTraceSearch = hasStructuredQuery && hasExplicitTraceItemDataset;
		const environmentFilter = formatEnvironmentFilter(params.environment);
		const explicitStructuredTraceQuery = shouldTrustStructuredTraceSearch ? appendSearchFilter(params.query ?? "", environmentFilter) : params.query ?? "";
		const canRunWithoutAgent = shouldTrustStructuredTraceSearch && hasExplicitFields && hasExplicitSort;
		if (hasAgentProvider() && !canRunWithoutAgent) {
			const parsed = await withProviderFallback({
				operation: "search_events.rewrite",
				fallback: () => ({
					dataset: inputDataset,
					query: params.query ?? "",
					fields: inputDataset === "replays" ? [] : params.fields ?? defaultFieldsForDataset(inputDataset),
					sort: explicitSort || defaultSortForDataset(inputDataset),
					environment: params.environment ?? null,
					timeRange: { statsPeriod: params.period ?? "14d" },
					explanation: ""
				}),
				run: async () => (await searchEventsAgent({
					query: buildAgentPrompt({
						query: params.query,
						dataset: inputDataset,
						fields: params.fields,
						sort: params.sort,
						statsPeriod: params.period,
						environment: params.environment
					}),
					organizationSlug,
					apiService,
					projectId
				})).result
			});
			const shouldTrustExplicitSearchParams = shouldTrustStructuredTraceSearch || hasStructuredQuery && parsed.dataset === inputDataset;
			if (!parsed.sort?.trim() && !(shouldTrustExplicitSearchParams && hasExplicitSort)) throw new UserInputError(`Search Events Agent response missing required 'sort' parameter. Received: ${JSON.stringify(parsed, null, 2)}. The agent must specify how to sort results (e.g., '-timestamp' for newest first).`);
			dataset = shouldTrustStructuredTraceSearch ? inputDataset : parsed.dataset;
			sentryQuery = shouldTrustStructuredTraceSearch ? choosePreservingRepairedQuery({
				originalQuery: params.query ?? "",
				repairedQuery: parsed.query,
				filter: environmentFilter
			}) : looksLikeSentrySearchSyntax(params.query) && isSemanticFilterDowngrade(params.query ?? "", parsed.query || "") ? params.query ?? "" : parsed.query || "";
			sortParam = shouldTrustExplicitSearchParams && explicitSort ? explicitSort : parsed.sort?.trim() || defaultSortForDataset(dataset);
			explanation = parsed.explanation;
			environment = params.environment ?? parsed.environment;
			timeParams = shouldTrustExplicitSearchParams && hasExplicitPeriod ? { statsPeriod: params.period } : parseAgentTimeRange(parsed.timeRange) ?? { statsPeriod: "14d" };
			if (dataset === "replays") fields = [];
			else fields = resolveEventFields({
				dataset,
				explicitFields: params.fields,
				agentFields: parsed.fields,
				trustExplicitFields: shouldTrustExplicitSearchParams
			});
		} else {
			dataset = inputDataset;
			sentryQuery = shouldTrustStructuredTraceSearch ? explicitStructuredTraceQuery : params.query ?? "";
			sortParam = explicitSort || defaultSortForDataset(dataset);
			timeParams = { statsPeriod: params.period ?? "14d" };
			fields = dataset === "replays" ? [] : params.fields ?? defaultFieldsForDataset(dataset);
		}
		if (dataset === "replays") {
			const replaySort = sortParam || DEFAULT_REPLAY_SORT;
			if (!isValidReplaySort(replaySort)) throw new UserInputError(`Invalid replay sort "${replaySort}". Use a supported replay sort like ${DEFAULT_REPLAY_SORT}, -count_errors, -count_rage_clicks, or -duration.`);
			const replayTimeParams = { ...timeParams };
			if (!replayTimeParams.statsPeriod && !replayTimeParams.start && !replayTimeParams.end) replayTimeParams.statsPeriod = DEFAULT_REPLAY_STATS_PERIOD;
			const replays = await apiService.searchReplays({
				organizationSlug,
				query: sentryQuery,
				limit: params.limit,
				projectId,
				sort: replaySort,
				environment: environment ?? void 0,
				...replayTimeParams
			});
			const replaySearchUrl = apiService.getReplaysSearchUrl(organizationSlug, {
				query: sentryQuery || void 0,
				projectSlugOrId: projectId,
				environment: environment ?? void 0,
				sort: replaySort,
				...replayTimeParams
			});
			getActiveSpan()?.setAttribute("gen_ai.tool.call.result.count", replays.length);
			return formatReplayResults({
				replays,
				inputQuery: params.query || sentryQuery || "recent replays",
				includeExplanation: params.includeExplanation,
				organizationSlug,
				apiService,
				searchUrl: replaySearchUrl,
				replayQuery: sentryQuery,
				sort: replaySort,
				environment,
				explanation,
				timeRange: replayTimeParams,
				executedSearch: {
					dataset,
					query: sentryQuery,
					fields: [],
					sort: replaySort,
					timeRange: replayTimeParams
				},
				experimentalMode: context.experimentalMode ?? false,
				availableToolNames: context.availableToolNames,
				directToolNames: context.directToolNames
			});
		}
		fields = augmentFieldsWithSort(fields, sortParam);
		const requestFields = buildRequestFields(dataset, fields);
		sentryQuery = applyEnvironmentToEventsQuery(dataset, sentryQuery, environment);
		const lastValidation = await validateEventsSearch(apiService, {
			organizationSlug,
			dataset,
			fields: requestFields,
			query: sentryQuery,
			sort: sortParam,
			projectId,
			environment: environment ?? void 0,
			...timeParams
		});
		recordEventsSearchValidationTelemetry({
			attempt: 0,
			validation: lastValidation
		});
		if (!lastValidation.valid) {
			const formatted = formatEventsValidationResults(lastValidation);
			throw new UserInputError(formatted ? `Search validation failed:\n${formatted}` : "Search validation failed.");
		}
		const finalRequestFields = buildRequestFields(dataset, fields);
		sentryQuery = applyEnvironmentToEventsQuery(dataset, sentryQuery, environment);
		const eventsResponse = await apiService.searchEvents({
			organizationSlug,
			query: sentryQuery,
			fields: finalRequestFields,
			limit: params.limit,
			projectId,
			dataset,
			sort: sortParam,
			...timeParams
		});
		const aggregateFunctions = fields.filter((field) => field.includes("(") && field.includes(")"));
		const groupByFields = fields.filter((field) => !field.includes("(") && !field.includes(")"));
		function isValidResponse(response) {
			return typeof response === "object" && response !== null;
		}
		function isValidEventArray(data) {
			return Array.isArray(data) && data.every((item) => typeof item === "object" && item !== null);
		}
		if (!isValidResponse(eventsResponse)) throw new Error("Invalid response format from Sentry API");
		const eventData = eventsResponse.data;
		if (!isValidEventArray(eventData)) throw new Error("Invalid event data format from Sentry API");
		getActiveSpan()?.setAttribute("gen_ai.tool.call.result.count", eventData.length);
		const conversationId = extractConversationIdFromSearchQuery(sentryQuery);
		const explorerUrl = conversationId ? apiService.getAIConversationUrl(organizationSlug, conversationId) : apiService.getEventsExplorerUrl(organizationSlug, sentryQuery, projectId, dataset, fields, sortParam, aggregateFunctions, groupByFields, timeParams.statsPeriod, timeParams.start, timeParams.end, eventData);
		const formatParams = {
			eventData,
			inputQuery: params.query || sentryQuery || `${dataset} events`,
			includeExplanation: params.includeExplanation,
			apiService,
			organizationSlug,
			explorerUrl,
			sentryQuery,
			fields,
			explanation,
			executedSearch: {
				dataset,
				query: sentryQuery,
				fields,
				sort: sortParam,
				timeRange: timeParams
			},
			experimentalMode: context.experimentalMode ?? false,
			availableToolNames: context.availableToolNames,
			directToolNames: context.directToolNames
		};
		switch (dataset) {
			case "errors": return formatErrorResults(formatParams);
			case "logs": return formatLogResults(formatParams);
			case "spans": return formatSpanResults(formatParams);
			case "profiles": return formatProfileResults(formatParams);
			default: return formatTraceMetricsResults(formatParams);
		}
	}
});

//#endregion
export { search_events_default };
//# sourceMappingURL=search-events-C8aVtdIY.js.map